import { ThreadArtifactKind, WorkflowStepKind } from '@digital-twins/shared-types'

import i18n from '@/i18n'

import { parseGateQuestions, type GateQuestion } from './gateArtifacts'

/**
 * Display-only category + preview for a written workflow artifact.
 *
 * Does not change stored `title` / `body`. A questions payload is detected by
 * shape, via `parseGateQuestions`, never a title string. The card line is the
 * opening of the body (preview), not a claimed headline.
 */

export type ArtifactCategory =
  | 'Report'
  | 'Creatives'
  | 'Brief'
  | 'Questions'
  | 'DeskResearch'
  | 'Digest'

export interface ArtifactDisplayLabel {
  category: ArtifactCategory | null
  headline: string
  /** A/B/C when more than one creative shares the rail. */
  letter?: string
}

export function localizeArtifactCategory(
  category: ArtifactCategory | null,
  letter?: string,
): string | null {
  if (!category) {
    return null
  }
  if (category === 'Report') {
    return i18n.t('library.type.report', { ns: 'app' })
  }
  if (category === 'Creatives') {
    if (letter) {
      return i18n.t('threads.detail.runPane.creativeLetter', {
        ns: 'app',
        letter,
      })
    }
    return i18n.t('threads.detail.runPane.creatives', { ns: 'app' })
  }
  if (category === 'Questions') {
    return i18n.t('threads.detail.runPane.questions', { ns: 'app' })
  }
  if (category === 'DeskResearch') {
    return i18n.t('threads.detail.runPane.deskResearch', { ns: 'app' })
  }
  if (category === 'Digest') {
    return i18n.t('threads.detail.runPane.digest', { ns: 'app' })
  }
  return i18n.t('threads.detail.runPane.brief', { ns: 'app' })
}

export interface ArtifactDisplayLabelInput {
  title: string
  body: string
  kind?: ThreadArtifactKind
  stepKind?: WorkflowStepKind
  /** Producing-step output slug when known (`market_evidence`, `daily_brief`). */
  slug?: string
  /**
   * Fan-out position among creatives in a set (0 = A).
   */
  index?: number
  /** Lettered set member (`A`, `B`, …). */
  letter?: string
}

const FIRST_HEADING = /^(#{1,6})\s+(.+)$/

export function artifactDisplayLabel(
  item: ArtifactDisplayLabelInput,
): ArtifactDisplayLabel {
  const questions = parseGateQuestions(item.body)
  if (questions !== null) {
    return {
      category: 'Questions',
      headline: questionsHeadline(questions, item.title),
    }
  }

  const letter = resolveCreativeLetter(item)

  return {
    category: resolveCategory(item),
    headline: extractPreview(item.body, item.title),
    ...(letter ? { letter } : {}),
  }
}

const MAX_QUESTIONS_IN_HEADLINE = 3
const MAX_QUESTION_LABEL_LENGTH = 40

function questionsHeadline(
  questions: Array<GateQuestion>,
  fallbackTitle: string,
): string {
  if (questions.length === 0) {
    return fallbackTitle
  }

  const shown = questions
    .slice(0, MAX_QUESTIONS_IN_HEADLINE)
    .map((question) => truncateQuestionLabel(question.title?.trim() || question.prompt))

  return questions.length > MAX_QUESTIONS_IN_HEADLINE
    ? `${shown.join(', ')}, …`
    : shown.join(', ')
}

function truncateQuestionLabel(value: string): string {
  const trimmed = value.trim()
  return trimmed.length > MAX_QUESTION_LABEL_LENGTH
    ? `${trimmed.slice(0, MAX_QUESTION_LABEL_LENGTH - 1)}…`
    : trimmed
}

function resolveCreativeLetter(
  item: ArtifactDisplayLabelInput,
): string | undefined {
  const fromItem = item.letter?.trim()
  if (fromItem) {
    return fromItem.toUpperCase()
  }
  if (item.index == null) {
    return undefined
  }
  if (item.kind === 'creative' || item.stepKind === 'prompt') {
    return String.fromCharCode(65 + (item.index % 26))
  }
  return undefined
}

function resolveCategory(
  item: ArtifactDisplayLabelInput,
): ArtifactCategory | null {
  if (item.kind === 'stimulus') {
    return null
  }
  /* Sweep / cited web search lands as living `other` (`market_evidence`).
     Same prefix whether the row was promoted that way or still tagged brief. */
  if (item.kind === 'other' || item.slug === 'market_evidence') {
    return 'DeskResearch'
  }
  if (item.kind === 'digest') {
    return 'Digest'
  }
  if (
    item.kind === 'report' ||
    item.kind === 'findings' ||
    item.kind === 'study_doc'
  ) {
    return 'Report'
  }
  if (item.kind === 'creative') {
    return 'Creatives'
  }
  if (item.kind === 'brief') {
    return 'Brief'
  }
  if (item.stepKind === 'report') {
    return 'Report'
  }
  if (item.index != null) {
    return 'Creatives'
  }
  if (item.stepKind === 'prompt') {
    return 'Brief'
  }
  return null
}

function extractPreview(body: string, fallbackTitle: string): string {
  const trimmed = body.trim()
  if (trimmed.length === 0) {
    return fallbackTitle
  }

  const firstLine = trimmed.split(/\r?\n/, 1)[0] ?? ''
  const heading = firstLine.match(FIRST_HEADING)
  if (heading) {
    const fromHeading = stripMarkdownMarkers(heading[2]).trim()
    return fromHeading.length > 0 ? fromHeading : fallbackTitle
  }

  const firstParagraph = trimmed.split(/\n\s*\n/, 1)[0] ?? trimmed
  const cleaned = stripMarkdownMarkers(firstParagraph).trim()
  const sentenceMatch = cleaned.match(/^(.+?[.!?])(?:\s|$)/)
  const fromSentence = (
    sentenceMatch ? sentenceMatch[1] : (cleaned.split(/\n/, 1)[0] ?? cleaned)
  ).trim()
  return fromSentence.length > 0 ? fromSentence : fallbackTitle
}

export function stripMarkdownMarkers(value: string): string {
  return value
    .replace(/^#{1,6}\s+/, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^>\s+/, '')
}

export function bodyOpensWithHeading(body: string): boolean {
  return FIRST_HEADING.test(body.trimStart().split(/\r?\n/, 1)[0] ?? '')
}

/**
 * The body without its leading heading. The card chrome already shows that
 * heading as the headline (`extractPreview` reads it from the same first
 * line), so rendering it again inside the body put the title on screen
 * twice. Only a heading on the very first line is removed, together with the
 * blank lines after it; a body that opens with prose is returned untouched.
 */
export function stripLeadingHeading(body: string): string {
  if (!bodyOpensWithHeading(body)) {
    return body
  }
  const lines = body.trimStart().split(/\r?\n/)
  lines.shift()
  while (lines.length > 0 && lines[0].trim() === '') {
    lines.shift()
  }
  return lines.join('\n')
}
